import { useRouter } from 'next/router';

import { Alert, Anchor, Box } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import {
  type MultiplexBuildingSessionListingCriterionWebDto,
  RentalManagerListingCriteriaType,
  type UserPropertyCriteriaGetCriteriaWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyCriteriaWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useIsOnReviewStep, useMultiplexDetails, useMultiplexListingFlow } from '../../../hooks/queries/multiplex';
import listingCriteriaUtils from '../../../utils/listingCriteriaUtils';

import IncomeToRentRatioFormField from '../IncomeToRentRatioFormField';
import MinCreditScoreFormField from '../MinCreditScoreFormField';

import type { PartialMessageProps } from '@zg-rentals/backend-styled-components';

type FinancialExpectationCriterionDto =
  | UserPropertyCriteriaGetCriteriaWebDto
  | MultiplexBuildingSessionListingCriterionWebDto;

const getPartialMessagesFromAlertLinks = (links: Array<PartialMessageProps> = []) => {
  const result: Array<PartialMessageProps> = [];
  links.forEach((link, index) => {
    result.push({ isBold: link.isBold, text: link.text, url: link.url });
    if (index !== links.length - 1) {
      result.push({ text: ' ' });
    }
  });
  return result;
};

const FinancialExpectationsFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });

  const { data: criteriaData } = useRentalManagerUserPropertyCriteriaWebApiHandler_get(
    { listingAlias },
    {
      enabled: Boolean(listingAlias),
      meta: {
        tags: ['users-properties-criteria-get'],
        context: { api: 'rental-manager', details: 'FinancialExpectationsFormField' },
      },
    },
  );

  const listingCriteria = criteriaData?.criteria ?? [];

  /**
   * Multiplex wizard steps read building-session criteria; review and non-multiplex use listing criteria.
   */
  const criteriaForSection: Array<FinancialExpectationCriterionDto> =
    isInMultiplexListingFlow && !isOnReviewStep && multiplexData?.listingCriteria?.criteria
      ? multiplexData.listingCriteria.criteria
      : listingCriteria;

  const criteriaPrice =
    isInMultiplexListingFlow && !isOnReviewStep && multiplexData?.listingCriteria?.price != null
      ? multiplexData.listingCriteria.price
      : (criteriaData?.price ?? 0);

  const getCriterion = (type: string) => listingCriteriaUtils.getCriteriaByType(criteriaForSection, type);

  const creditScoreCriterion = getCriterion(RentalManagerListingCriteriaType.CREDIT_SCORE_MIN);
  const incomeToRentRatioCriterion = getCriterion(RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO);

  const isCreditScoreEnabled = creditScoreCriterion?.isEnabled ?? true;
  const isRentToIncomeRatioEnabled = incomeToRentRatioCriterion?.isEnabled ?? true;

  const creditScoreAlert = creditScoreCriterion?.alert ?? null;
  const rentToIncomeRatioAlert = incomeToRentRatioCriterion?.alert ?? null;

  const multiplexCriteria = isInMultiplexListingFlow ? criteriaForSection : undefined;

  return (
    <Box data-testid="FinancialExpectationsFormField">
      {isCreditScoreEnabled && creditScoreAlert && (
        <Box data-testid="FinancialExpectationsFormField-CreditScoreAlert" css={{ marginBottom: '400' }}>
          <Alert tone="warning">
            <Box css={{ marginBottom: '200' }}>{creditScoreAlert.body}</Box>
            <PartialMessage
              linkComponent={Anchor}
              partialMessages={getPartialMessagesFromAlertLinks(creditScoreAlert.links ?? [])}
            />
          </Alert>
        </Box>
      )}

      {isRentToIncomeRatioEnabled && rentToIncomeRatioAlert && (
        <Box data-testid="FinancialExpectationsFormField-IncomeRatioAlert" css={{ marginBottom: '400' }}>
          <Alert tone="warning">
            <Box css={{ marginBottom: '200' }}>{rentToIncomeRatioAlert.body}</Box>
            <PartialMessage
              linkComponent={Anchor}
              partialMessages={getPartialMessagesFromAlertLinks(rentToIncomeRatioAlert.links ?? [])}
            />
          </Alert>
        </Box>
      )}

      {isRentToIncomeRatioEnabled && (
        <IncomeToRentRatioFormField
          criteria={multiplexCriteria}
          incomeToRentRatioMax={incomeToRentRatioCriterion?.max}
          price={criteriaPrice}
        />
      )}

      {isCreditScoreEnabled && (
        <MinCreditScoreFormField criteria={multiplexCriteria} creditScoreMax={creditScoreCriterion?.max} />
      )}
    </Box>
  );
};

export default FinancialExpectationsFormField;
